import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { BrandMarquee } from "@/components/site/Marquee";
import HeroVideo from "@/components/site/HeroVideo";
import EventCard from "@/components/site/EventCard";

const CHAPTERS = [
  { n: "01", t: "We don't do ordinary nights.", d: "Every MARKIE 2 FRESH event is engineered end-to-end — the room, the sound, the lineup, the people. No filler. Just peak-hour energy from doors to lights-up." },
  { n: "02", t: "The city is our dancefloor.", d: "Rooftops, warehouses, waterfronts. We find the spaces New York forgot and turn them into the only place you want to be that weekend." },
  { n: "03", t: "Your ticket is your key.", d: "Tap to buy, get an instant QR pass, skip the line at the door. It's ticketing that works as hard as the party does." },
];

export default function Home() {
  const [events, setEvents] = useState([]);
  const [posts, setPosts] = useState([]);
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.2], [0, -80]);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data.slice(0, 4))).catch(() => {});
    api.get("/instagram/feed?limit=6").then((r) => setPosts(r.data.posts || [])).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex flex-col justify-center pt-28 pb-10 overflow-hidden">
        <HeroVideo />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-cream via-brand-cream/85 to-brand-cream/25 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-cream/90 via-transparent to-brand-cream/20 pointer-events-none" />

        <motion.div style={{ y: heroY }} className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 w-full">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-brand-orange mb-6"
          >
            NYC Party Promoter · Est. Nightlife
          </motion.div>

          <div className="overflow-hidden max-w-[92vw] md:max-w-[880px] mt-2">
            <motion.img
              src="/logo.png"
              alt="MARKIE 2 FRESH"
              data-testid="hero-logo"
              initial={{ y: "112%", opacity: 0 }}
              animate={{ y: "0%", opacity: 1 }}
              transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
              className="w-full h-auto drop-shadow-[0_14px_40px_rgba(0,0,0,0.18)]"
            />
          </div>

          <div className="mt-8 grid md:grid-cols-[1fr_auto] gap-8 items-end">
            <Reveal delay={0.5} className="max-w-md text-brand-ink/70 text-lg leading-relaxed">
              Sleek, high-energy events across New York City. Buy a ticket, get your QR pass,
              and we'll see you on the dancefloor.
            </Reveal>
            <Reveal delay={0.6}>
              <Link
                to="/tickets"
                data-testid="hero-cta"
                className="group inline-flex items-center gap-3 rounded-full bg-brand-ink text-white pl-8 pr-3 py-3 relative overflow-hidden"
              >
                <span className="absolute inset-0 bg-brand-orange scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
                <span className="relative font-mono text-sm uppercase tracking-[0.2em]">Get Tickets</span>
                <span className="relative w-10 h-10 rounded-full bg-brand-orange group-hover:bg-white flex items-center justify-center transition-colors">
                  <ArrowDown size={18} className="text-white group-hover:text-brand-ink" />
                </span>
              </Link>
            </Reveal>
          </div>
        </motion.div>
      </section>

      <BrandMarquee />

      {/* MANIFESTO */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-28 md:py-40">
        <Reveal className="font-mono text-xs uppercase tracking-[0.3em] text-brand-ink/40 mb-16">
          / The Manifesto
        </Reveal>
        <div className="space-y-24 md:space-y-32">
          {CHAPTERS.map((c, i) => (
            <div key={c.n} className="grid md:grid-cols-[auto_1fr] gap-8 md:gap-20 items-start">
              <Reveal>
                <div className="font-head font-extrabold text-[18vw] md:text-[9vw] leading-none text-brand-orange">
                  {c.n}
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-head font-bold text-4xl md:text-6xl leading-[0.95] tracking-tight uppercase max-w-2xl">
                  {c.t}
                </h2>
                <p className="mt-6 max-w-lg text-brand-ink/60 text-lg leading-relaxed">{c.d}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED EVENTS */}
      <section className="bg-brand-ink text-white py-28 md:py-40">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand-orange mb-4">/ What's Next</div>
              <h2 className="font-head font-extrabold text-5xl md:text-8xl uppercase leading-none">Upcoming</h2>
            </div>
            <Link to="/events" className="font-mono text-xs uppercase tracking-[0.2em] text-white/60 hover:text-brand-orange transition-colors">
              View all events →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {events.map((e, i) => (
              <EventCard key={e.id} event={e} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-28 md:py-40">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand-ink/40 mb-4">/ The Feed</div>
            <h2 className="font-head font-extrabold text-5xl md:text-8xl uppercase leading-none">
              @markie<span className="text-brand-orange">2</span>fresh
            </h2>
          </div>
          <a href="https://instagram.com" target="_blank" rel="noreferrer" className="font-mono text-xs uppercase tracking-[0.2em] text-brand-ink/60 hover:text-brand-orange transition-colors">
            Follow on Instagram →
          </a>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {posts.map((p, i) => (
            <Reveal key={p.id || i} delay={(i % 3) * 0.08}>
              <a href={p.permalink || "#"} target="_blank" rel="noreferrer" className="group block relative aspect-square overflow-hidden bg-brand-ink">
                <img src={p.image_url} alt={p.caption || "Instagram post"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-brand-orange/0 group-hover:bg-brand-orange/20 transition-colors" />
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* GUEST LIST CTA */}
      <section className="relative overflow-hidden py-28 md:py-40 bg-brand-orange text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 relative z-10">
          <Reveal>
            <h2 className="font-head font-extrabold text-6xl md:text-9xl uppercase leading-[0.85] max-w-4xl">
              Join the guest list.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-xl text-white/80 text-lg">
              Drop your email and birthday — get first access to tickets, secret shows and
              birthday surprises before anyone else.
            </p>
            <Link
              to="/guest-list"
              data-testid="home-guestlist-cta"
              className="inline-flex mt-10 items-center gap-2 rounded-full bg-brand-ink text-white px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-black transition-colors"
            >
              Sign me up →
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
